import { useState, useEffect, useRef } from "react";
import styled from "styled-components";
import { useTranslation } from "react-i18next";
import { toast } from "react-toastify";
import Typography from "../Typography";
import Button from "../Button/Button";
import StripeLogo from "../Icons/StripeLogo";
import StripeConnectIcon from "../Icons/StripeConnectIcon";
import ArrowRightUpIcon from "../Icons/ArrowRightUpIcon";
import RefreshIcon from "../Icons/RefreshIcon";
import InfoCircleOutlineIcon from "../Icons/InfoCircleOutlineIcon";
import StripeAccountReactivation from "../StripeAccountReactivation";
import StripeConnectionOptionsModal from "../Modal/StripeConnectionOptionsModal";
import Modal from "../Modal/Modal";
import { useStripeDataCache } from "../../hooks/useStripeDataCache";
import axiosClient from "../../api/axiosClient";
import { theme } from "../../theme";
import { formatTimeOnly } from "../../utils/dateUtils";

const StripeManagement = ({
	user,
	stripeStatus,
	refreshStripeStatus,
	setUser,
	variant = "full", // 'full' | 'simple'
	showMetrics = true,
	showReactivation = true,
	onStripeConnected,
	onStripeDisconnected,
	$plain = false, // Prop para remover border y border-radius
}) => {
	const { t } = useTranslation(["stripeManagement"]);
	const [isActivating, setIsActivating] = useState(false);
	const [isDisconnecting, setIsDisconnecting] = useState(false);
	const [showReactivationModal, setShowReactivationModal] = useState(false);
	const [showConnectionOptionsModal, setShowConnectionOptionsModal] =
		useState(false);
	const [showDisconnectConfirmModal, setShowDisconnectConfirmModal] =
		useState(false);
	const [hasAvailableAccounts, setHasAvailableAccounts] = useState(false);
	const [isCreatingNew, setIsCreatingNew] = useState(false);
	const [isConnectingExisting, setIsConnectingExisting] = useState(false);

	// Hook de caché para datos de Stripe (solo si showMetrics es true)
	const {
		data: stripeData,
		loading,
		refresh: refreshStripeData,
		clearCache,
		cacheInfo,
		lastFetch,
	} = useStripeDataCache(user, stripeStatus, showMetrics);

	// Ref para evitar múltiples llamadas a refreshStripeStatus
	const isInitialLoad = useRef(true);

	useEffect(() => {
		if (user && isInitialLoad.current) {
			refreshStripeStatus(user.id);
			checkAvailableAccounts();
			isInitialLoad.current = false;
		}
	}, [refreshStripeStatus, user?.id]);

	// Función para verificar si hay cuentas disponibles
	const checkAvailableAccounts = async () => {
		try {
			const response = await axiosClient.get("/api/stripe/available-accounts");
			if (response.data.success && response.data.accounts.length > 0) {
				setHasAvailableAccounts(true);
			} else {
				setHasAvailableAccounts(false);
			}
		} catch (error) {
			console.error("Error checking available accounts:", error);
			setHasAvailableAccounts(false);
		}
	};

	// Detectar cuando el usuario regresa de Stripe (con debounce)
	useEffect(() => {
		let focusTimeout;
		const handleWindowFocus = () => {
			if (user && !showReactivationModal) {
				// Debounce para evitar múltiples llamadas
				// Solo refrescar si no hay modal de reactivación abierto
				clearTimeout(focusTimeout);
				focusTimeout = setTimeout(() => {
					refreshStripeStatus(user.id);
				}, 1000);
			}
		};

		window.addEventListener("focus", handleWindowFocus);
		return () => {
			window.removeEventListener("focus", handleWindowFocus);
			clearTimeout(focusTimeout);
		};
	}, [user, refreshStripeStatus, showReactivationModal]); // Removido showConnectionOptionsModal para evitar reset del estado

	const handleConnectStripe = async () => {
		try {
			// Cerrar cualquier modal que pueda estar abierto
			setShowReactivationModal(false);

			// Mostrar modal de opciones de conexión
			setShowConnectionOptionsModal(true);
		} catch (error) {
			console.error("Error in handleConnectStripe:", error);
		}
	};

	const handleCreateNewAccount = async () => {
		try {
			setIsCreatingNew(true);
			const { data } = await axiosClient.post(
				"/api/stripe/create-or-connect-account",
				{ userId: user.id },
			);
			if (data.onboardingUrl) {
				// Abrir en nueva ventana para evitar que Stripe use información de sesión previa
				// que puede pre-llenar y deshabilitar el campo de email
				window.open(data.onboardingUrl, "_blank");
				setShowConnectionOptionsModal(false);
			} else {
				toast.success("Cuenta de Stripe ya conectada.");
				await refreshStripeStatus(user.id);
				// Forzar actualización de datos después de conectar
				if (showMetrics) {
					setTimeout(() => {
						refreshStripeData(true);
					}, 1000);
				}
				setShowConnectionOptionsModal(false);
				onStripeConnected && onStripeConnected();
			}
		} catch (error) {
			console.error("Error al crear nueva cuenta de Stripe:", error);
			toast.error("No se pudo crear la cuenta de Stripe, intente de nuevo.");
		} finally {
			setIsCreatingNew(false);
		}
	};

	const handleConnectExistingAccount = async () => {
		try {
			setIsConnectingExisting(true);
			const { data } = await axiosClient.post(
				"/api/stripe/oauth/connect-existing",
				{ userId: user.id },
			);
			if (data.success && data.oauthUrl) {
				// Abrir OAuth en nueva ventana
				window.open(data.oauthUrl, "_blank");
				setShowConnectionOptionsModal(false);
				toast.info(
					"Completa la autorización en la ventana de Stripe que se abrió.",
				);
			} else {
				toast.error("No se pudo generar la URL de conexión.");
			}
		} catch (error) {
			console.error("Error al conectar cuenta existente:", error);
			toast.error("No se pudo conectar la cuenta existente, intente de nuevo.");
		} finally {
			setIsConnectingExisting(false);
		}
	};

	const handleShowReactivationModal = () => {
		setShowConnectionOptionsModal(false);
		setShowReactivationModal(true);
	};

	const handleCompleteVerification = async () => {
		try {
			setIsActivating(true);
			const { data } = await axiosClient.post(
				"/api/stripe/create-account-link",
				{ userId: user.id },
			);
			if (data.success && data.url) {
				// Abrir en nueva ventana para evitar problemas de sesión con Stripe
				window.open(data.url, "_blank");
			} else {
				toast.info(
					data.message || "No se generó un nuevo link de verificación",
				);
			}
		} catch (error) {
			console.error("Error al generar link de verificación:", error);
			toast.error(
				"Hubo un error al tratar de completar la verificación en Stripe.",
			);
		} finally {
			setIsActivating(false);
		}
	};

	const handleOpenStripe = () => {
		window.open("https://dashboard.stripe.com", "_blank");
	};

	const openDisconnectConfirmModal = () => {
		setShowDisconnectConfirmModal(true);
	};

	const closeDisconnectConfirmModal = () => {
		setShowDisconnectConfirmModal(false);
	};

	const handleDisconnectStripe = async () => {
		try {
			setIsDisconnecting(true);
			const { data } = await axiosClient.post("/api/stripe/disconnect-account");
			if (data.success) {
				toast.success("Cuenta de Stripe desvinculada.");
				setShowDisconnectConfirmModal(false);
				setUser(data.user);
				// Limpiar caché después de desconectar (sin mostrar toast adicional)
				if (showMetrics) {
					clearCache();
				}
				refreshStripeStatus(user.id);
				// Verificar cuentas disponibles después de desvincular
				checkAvailableAccounts();
				onStripeDisconnected && onStripeDisconnected();
			} else {
				toast.error(
					data.message || "No se pudo desvincular la cuenta de Stripe.",
				);
			}
		} catch (error) {
			console.error("Error desvinculando Stripe:", error);
			toast.error("Error al desvincular Stripe.");
		} finally {
			setIsDisconnecting(false);
		}
	};

	const handleAccountReactivated = (data) => {
		setShowReactivationModal(false);
		setUser(data.user);
		refreshStripeStatus(user.id);
		// Verificar cuentas disponibles después de reactivar
		checkAvailableAccounts();
		onStripeConnected && onStripeConnected();
	};

	const formatCurrency = (amount) => {
		return new Intl.NumberFormat("es-ES", {
			style: "currency",
			currency: "EUR",
		}).format(amount);
	};

	const hasFinancialHistory = Boolean(
		stripeData.totalRevenue ||
		stripeData.totalTicketsSold ||
		stripeData.availableBalance ||
		stripeData.pendingBalance ||
		cacheInfo.hasCache,
	);
	const isSimpleVariant = variant === "simple";
	const connectedDisplayName =
		stripeData.accountInfo?.businessName ||
		[user?.name, user?.lastName].filter(Boolean).join(" ");
	const connectedDisplayEmail = stripeData.accountInfo?.email || user?.email;

	const renderSimpleStripeCard = ({
		icon,
		title,
		statusLabel,
		statusTone = "warning",
		bodyTitle,
		bodyDescription,
		details,
		primaryLabel,
		primaryLoadingLabel,
		onPrimary,
		primaryDisabled = false,
		primaryVariant = "primary",
		secondaryLabel,
		onSecondary,
		footerNote,
		stackHeader = false,
	}) => (
		<SimpleStripeSurface>
			<SimpleStripeCard>
				<SimpleStripeHeader $stacked={stackHeader}>
					<SimpleStripeIconSlot>{icon}</SimpleStripeIconSlot>
					<SimpleStripeHeaderCopy>
						<Typography as="h3" $variant="body-1-semibold">
							{title}
						</Typography>
						{statusLabel && (
							<StatusContainer>
								<StatusIndicator $isActive={statusTone === "success"} />
								<Typography
									as="span"
									$variant="body-2-medium"
									color={
										statusTone === "success"
											? theme.colors.successMain
											: theme.colors.warningMain
									}
								>
									{statusLabel}
								</Typography>
							</StatusContainer>
						)}
					</SimpleStripeHeaderCopy>
				</SimpleStripeHeader>

				<SimpleStripeBody>
					{bodyTitle && (
						<Typography as="p" $variant="body-1-semibold">
							{bodyTitle}
						</Typography>
					)}
					{bodyDescription && (
						<Typography
							as="p"
							$variant="body-1-medium"
							$variantMobile="body-2-medium"
							$variantTablet="body-2-medium"
							color={theme.colors.defaultStrong}
						>
							{bodyDescription}
						</Typography>
					)}
					{details && (
						<Typography
							as="p"
							$variant="body-2-medium"
							color={theme.colors.defaultWeak}
						>
							{details}
						</Typography>
					)}
				</SimpleStripeBody>

				<SimpleStripeActions $hasSecondary={Boolean(secondaryLabel)}>
					<ActionButton
						onClick={onPrimary}
						$variant={primaryVariant}
						size="l"
						sizeTablet="l"
						sizeMobile="m"
						disabled={primaryDisabled}
					>
						{primaryDisabled && primaryLoadingLabel
							? primaryLoadingLabel
							: primaryLabel}
						<ArrowRightUpIcon
							fill={
								primaryVariant === "outline"
									? theme.colors.defaultMain
									: theme.colors.inverseMain
							}
						/>
					</ActionButton>
					{secondaryLabel && (
						<DisconnectButton
							type="button"
							$variant="ghostDanger"
							size="l"
							sizeTablet="l"
							sizeMobile="m"
							onClick={onSecondary}
						>
							{secondaryLabel}
						</DisconnectButton>
					)}
				</SimpleStripeActions>
			</SimpleStripeCard>
			{footerNote && (
				<SimpleStripeNote>
					<InfoCircleOutlineIcon
						width={20}
						height={20}
						fill={theme.colors.defaultWeak}
						aria-hidden="true"
					/>
					{/* <InfoDot aria-hidden="true" /> */}
					<Typography
						as="p"
						$variant="body-2-medium"
						color={theme.colors.defaultWeak}
					>
						{footerNote}
					</Typography>
				</SimpleStripeNote>
			)}
		</SimpleStripeSurface>
	);

	const renderMetricsCard = () => {
		if (!(variant === "full" && showMetrics)) {
			return null;
		}

		if (
			!(stripeStatus.hasStripeAccount && stripeStatus.chargesEnabled) &&
			!hasFinancialHistory &&
			!loading
		) {
			return null;
		}

		return (
			<MetricsCard $plain={$plain}>
				<RefreshButton
					$variant="ghost"
					$iconOnly
					onClick={() => refreshStripeData(true)}
					disabled={loading}
					size="l"
					sizeTablet="l"
					sizeMobile="m"
				>
					<RefreshIcon fill={theme.colors.defaultMain} width={20} height={20} />
				</RefreshButton>
				<MetricsGrid>
					<MetricItem>
						<Typography
							as="p"
							$variant="body-2-medium"
							color={theme.colors.defaultWeak}
						>
							{t("stripeManagement:metrics.last30Days")}
						</Typography>
						<Typography as="p" $variant="title-4-semibold">
							{formatCurrency(stripeData.last30DaysRevenue)}
						</Typography>
					</MetricItem>
					<MetricItem>
						<Typography
							as="p"
							$variant="body-2-medium"
							color={theme.colors.defaultWeak}
						>
							{t("stripeManagement:metrics.allTime")}
						</Typography>
						<Typography as="p" $variant="title-4-semibold">
							{formatCurrency(stripeData.totalRevenue)}
						</Typography>
					</MetricItem>
					<MetricItem>
						<Typography
							as="p"
							$variant="body-2-medium"
							color={theme.colors.defaultWeak}
						>
							{t("stripeManagement:metrics.ticketsSold")}
						</Typography>
						<Typography as="p" $variant="title-4-semibold">
							{stripeData.totalTicketsSold || stripeData.ticketsSold}
						</Typography>
					</MetricItem>
					<MetricItem>
						<Typography
							as="p"
							$variant="body-2-medium"
							color={theme.colors.defaultWeak}
						>
							{t("stripeManagement:metrics.platformFee")}
						</Typography>
						<Typography as="p" $variant="title-4-semibold">
							{formatCurrency(stripeData.platformFee || 0)}
						</Typography>
					</MetricItem>
					<MetricItem>
						<Typography
							as="p"
							$variant="body-2-medium"
							color={theme.colors.defaultWeak}
						>
							{t("stripeManagement:metrics.availableBalance")}
						</Typography>
						<Typography as="p" $variant="title-4-semibold">
							{formatCurrency(stripeData.availableBalance || 0)}
						</Typography>
					</MetricItem>
					<MetricItem>
						<Typography
							as="p"
							$variant="body-2-medium"
							color={theme.colors.defaultWeak}
						>
							{t("stripeManagement:metrics.pendingBalance")}
						</Typography>
						<Typography as="p" $variant="title-4-semibold">
							{formatCurrency(stripeData.pendingBalance || 0)}
						</Typography>
					</MetricItem>
				</MetricsGrid>

				{(process.env.NODE_ENV === "development" ||
					process.env.NODE_ENV === "staging") && (
						<DebugInfo>
							<Typography $variant="caption-semibold">Debug Info:</Typography>
							<Typography
								as="p"
								$variant="body-3-regular"
								color={theme.colors.defaultWeak}
							>
								User ID: {user?.id}
								<br />
								Has Stripe Account: {stripeStatus.hasStripeAccount ? "✅" : "❌"}
								<br />
								Charges Enabled: {stripeStatus.chargesEnabled ? "✅" : "❌"}
								<br />
								Details Submitted: {stripeStatus.detailsSubmitted ? "✅" : "❌"}
								<br />
								Payouts Enabled: {stripeStatus.payoutsEnabled ? "✅" : "❌"}
								<br />
								Account ID: {stripeStatus.accountId || "N/A"}
								<br />
								Modo:{" "}
								{user?.stripeConnectedAccountId?.startsWith("acct_test_")
									? "Test"
									: "Live"}
								<br />
								Loading: {loading ? "⏳" : "✅"}
								<br />
								Stripe Status Loading: {stripeStatus.loading ? "⏳" : "✅"}
								<br />
							</Typography>
							<Typography $variant="caption-semibold">Cache Info:</Typography>
							<Typography
								as="p"
								$variant="body-3-regular"
								color={theme.colors.defaultWeak}
							>
								Cache Status: {cacheInfo.isValid ? "✅ Valid" : "❌ Invalid"}
								<br />
								Cache Age: {cacheInfo.isStale ? "🟡 Stale" : "🟢 Fresh"}
								<br />
								Last Fetch:{" "}
								{lastFetch ? formatTimeOnly(lastFetch, "es") : "Never"}
								<br />
								Cache Key: {cacheInfo.cacheKey || "N/A"}
							</Typography>
						</DebugInfo>
					)}
			</MetricsCard>
		);
	};

	// Función para renderizar el contenido principal
	const renderMainContent = () => {
		// Caso 1: Sin cuenta de Stripe
		if (!stripeStatus.hasStripeAccount) {
			if (isSimpleVariant) {
				return renderSimpleStripeCard({
					icon: <StripeConnectIcon width={90} height={40} />,
					title: t("stripeManagement:noAccount.title"),
					bodyDescription: t("stripeManagement:noAccount.description"),
					primaryLabel: t("stripeManagement:noAccount.button"),
					primaryLoadingLabel: t("stripeManagement:noAccount.buttonLoading"),
					onPrimary: handleConnectStripe,
					primaryDisabled: loading,
					footerNote: t("stripeManagement:noAccount.note"),
					stackHeader: true,
				});
			}

			const noAccountCard = (
				<NoAccountCard $plain={$plain}>
					<Wrapper>
						<StripeHeader>
							<StripeIconContainer>
								<StripeLogo />
							</StripeIconContainer>
							<div>
								<Typography
									as="h3"
									$variant="body-1-semibold"
									$variantMobile="body-2-semibold"
								>
									{t("stripeManagement:noAccount.title")}
								</Typography>
								<StatusContainer>
									<StatusIndicator $isActive={false} />
									<Typography
										as="span"
										$variant="body-2-medium"
										color={theme.colors.warningMain}
									>
										{t("stripeManagement:status.notConnected")}
									</Typography>
								</StatusContainer>
							</div>
						</StripeHeader>
						<StripeExplainer>
							<Typography
								as="p"
								$variant="body-1-semibold"
								color={theme.colors.defaultMain}
							>
								{t("stripeManagement:noAccount.title")}
							</Typography>
							<Typography
								as="p"
								$variant="body-3-medium"
								color={theme.colors.defaultWeak}
							>
								{t("stripeManagement:noAccount.description")}
							</Typography>
						</StripeExplainer>
					</Wrapper>

					<ButtonContainer>
						<Button
							size="l"
							sizeTablet="l"
							sizeMobile="m"
							onClick={handleConnectStripe}
							disabled={loading}
							$variant="primary"
						>
							{loading
								? t("stripeManagement:noAccount.buttonLoading")
								: t("stripeManagement:noAccount.button")}
						</Button>
					</ButtonContainer>
				</NoAccountCard>
			);

			if (
				variant === "full" &&
				showMetrics &&
				(hasFinancialHistory || loading)
			) {
				return (
					<StripeManagementContainer>
						{noAccountCard}
						{renderMetricsCard()}
					</StripeManagementContainer>
				);
			}

			return noAccountCard;
		}

		// Caso 2: Acceso a la cuenta revocado (plataforma sin permisos)
		if (stripeStatus.hasStripeAccount && stripeStatus.accountAccessRevoked) {
			if (isSimpleVariant) {
				return renderSimpleStripeCard({
					icon: <StripeLogo width={48} height={48} />,
					title: t("stripeManagement:accessRevoked.title"),
					statusLabel: t("stripeManagement:status.notConnected"),
					bodyTitle: t("stripeManagement:accessRevoked.bodyTitle"),
					bodyDescription: t("stripeManagement:accessRevoked.description"),
					primaryLabel: t("stripeManagement:accessRevoked.button"),
					primaryLoadingLabel: t("stripeManagement:noAccount.buttonLoading"),
					onPrimary: handleConnectStripe,
					primaryDisabled: loading,
				});
			}

			const revokedCard = (
				<NoAccountCard $plain={$plain}>
					<Wrapper>
						<StripeHeader>
							<StripeIconContainer>
								<StripeLogo />
							</StripeIconContainer>
							<div>
								<Typography
									as="h3"
									$variant="body-1-semibold"
									$variantMobile="body-2-semibold"
								>
									{t("stripeManagement:accessRevoked.title")}
								</Typography>
								<StatusContainer>
									<StatusIndicator $isActive={false} />
									<Typography
										as="span"
										$variant="body-2-medium"
										color={theme.colors.warningMain}
									>
										{t("stripeManagement:status.notConnected")}
									</Typography>
								</StatusContainer>
							</div>
						</StripeHeader>
						<StripeExplainer>
							<Typography
								as="p"
								$variant="body-3-medium"
								color={theme.colors.defaultWeak}
							>
								{t("stripeManagement:accessRevoked.description")}
							</Typography>
						</StripeExplainer>
					</Wrapper>
					<ButtonContainer>
						<Button
							size="l"
							sizeTablet="l"
							sizeMobile="m"
							onClick={handleConnectStripe}
							disabled={loading}
							$variant="primary"
						>
							{loading
								? t("stripeManagement:noAccount.buttonLoading")
								: t("stripeManagement:accessRevoked.button")}
						</Button>
					</ButtonContainer>
				</NoAccountCard>
			);

			if (
				variant === "full" &&
				showMetrics &&
				(hasFinancialHistory || loading)
			) {
				return (
					<StripeManagementContainer>
						{revokedCard}
						{renderMetricsCard()}
					</StripeManagementContainer>
				);
			}

			return revokedCard;
		}

		// Caso 3: Cuenta pendiente de verificación
		if (stripeStatus.hasStripeAccount && !stripeStatus.chargesEnabled) {
			if (isSimpleVariant) {
				return renderSimpleStripeCard({
					icon: <StripeLogo width={48} height={48} />,
					title: t("stripeManagement:hasAccount.headerTitle"),
					statusLabel: t("stripeManagement:status.pending"),
					bodyTitle: t("stripeManagement:hasAccount.title"),
					bodyDescription: t("stripeManagement:hasAccount.description"),
					primaryLabel: t("stripeManagement:hasAccount.button"),
					primaryLoadingLabel: t("stripeManagement:hasAccount.buttonLoading"),
					onPrimary: handleCompleteVerification,
					primaryDisabled: isActivating,
					secondaryLabel: t("stripeManagement:hasAccount.disconnect"),
					onSecondary: openDisconnectConfirmModal,
				});
			}

			const pendingVerificationCard = (
				<PendingVerificationCard $plain={$plain}>
					<Wrapper>
						<StripeHeader>
							<StripeIconContainer>
								<StripeLogo />
							</StripeIconContainer>
							<div>
								<Typography
									as="h3"
									$variant="body-1-semibold"
									$variantMobile="body-2-semibold"
								>
									{t("stripeManagement:hasAccount.title")}
								</Typography>
								<StatusContainer>
									<StatusIndicator $isActive={false} />
									<Typography
										as="span"
										$variant="body-2-medium"
										color={theme.colors.warningMain}
									>
										{t("stripeManagement:status.pending")}
									</Typography>
								</StatusContainer>
							</div>
						</StripeHeader>
						<StripeExplainer>
							<Typography
								as="p"
								$variant="body-1-semibold"
								color={theme.colors.defaultMain}
							>
								{t("stripeManagement:hasAccount.title")}
							</Typography>
							<Typography
								as="p"
								$variant="body-3-medium"
								color={theme.colors.defaultWeak}
							>
								{t("stripeManagement:hasAccount.description")}
							</Typography>
							{/* Stripe requiere algunos datos para habilitarte los cobros. */}

							<Typography
								as="p"
								$variant="body-3-medium"
								color={theme.colors.defaultWeak}
							>
								Stripe ID: {user?.stripeConnectedAccountId}
							</Typography>
						</StripeExplainer>
					</Wrapper>

					<ButtonContainer>
						<Button
							onClick={handleCompleteVerification}
							$variant=""
							size="l"
							sizeTablet="l"
							sizeMobile="m"
							disabled={isActivating}
						>
							{isActivating
								? t("stripeManagement:hasAccount.buttonLoading")
								: t("stripeManagement:hasAccount.button")}
						</Button>
						<Button
							onClick={openDisconnectConfirmModal}
							$variant="ghostDanger"
							size="l"
							sizeTablet="l"
							sizeMobile="m"
						>
							{t("stripeManagement:hasAccount.disconnect")}
						</Button>
					</ButtonContainer>
				</PendingVerificationCard>
			);

			if (
				variant === "full" &&
				showMetrics &&
				(hasFinancialHistory || loading)
			) {
				return (
					<StripeManagementContainer>
						{pendingVerificationCard}
						{renderMetricsCard()}
					</StripeManagementContainer>
				);
			}

			return pendingVerificationCard;
		}

		// Caso 3: Cuenta activa
		if (stripeStatus.hasStripeAccount && stripeStatus.chargesEnabled) {
			if (isSimpleVariant) {
				return renderSimpleStripeCard({
					icon: <StripeLogo width={48} height={48} />,
					title: t("stripeManagement:chargesEnabled.title"),
					statusLabel: t("stripeManagement:status.active"),
					statusTone: "success",
					bodyTitle: t("stripeManagement:chargesEnabled.description"),
					details: [connectedDisplayName, connectedDisplayEmail]
						.filter(Boolean)
						.join(" · "),
					primaryLabel: t("stripeManagement:chargesEnabled.dashboard"),
					onPrimary: handleOpenStripe,
					primaryVariant: "outline",
					secondaryLabel: t("stripeManagement:chargesEnabled.disconnect"),
					onSecondary: openDisconnectConfirmModal,
				});
			}

			if (variant === "full" && showMetrics) {
				return (
					<StripeManagementContainer>
						{/* Bloque izquierdo - Información de Stripe */}
						<StripeInfoCard $plain={$plain}>
							<Wrapper>
								<StripeHeader>
									<StripeIconContainer>
										<StripeLogo />
									</StripeIconContainer>
									<div>
										<Typography
											as="h3"
											$variant="body-1-semibold"
											$variantMobile="body-2-semibold"
										>
											{t("stripeManagement:chargesEnabled.title")}
										</Typography>
										<StatusContainer>
											<StatusIndicator $isActive={true} />
											<Typography
												as="span"
												$variant="body-2-medium"
												color={theme.colors.successMain}
											>
												{t("stripeManagement:status.active")}
											</Typography>
										</StatusContainer>
									</div>
								</StripeHeader>
								<StripeExplainer>
									<Typography
										as="p"
										$variant="body-1-semibold"
										color={theme.colors.defaultMain}
									>
										{t("stripeManagement:chargesEnabled.description")}
									</Typography>
									{stripeData.accountInfo && (
										<>
											<Typography
												as="p"
												$variant="body-1-medium"
												color={theme.colors.defaultWeak}
											>
												{t("stripeManagement:account.business")}:{" "}
												{stripeData.accountInfo.businessName}
											</Typography>
											<Typography
												as="p"
												$variant="body-1-medium"
												color={theme.colors.defaultWeak}
											>
												{t("stripeManagement:account.email")}:{" "}
												{stripeData.accountInfo.email}
											</Typography>
										</>
									)}
									<Typography
										as="p"
										$variant="body-3-medium"
										color={theme.colors.defaultWeak}
									>
										{t("stripeManagement:account.stripeId")}:{" "}
										{user?.stripeConnectedAccountId}
									</Typography>
								</StripeExplainer>
							</Wrapper>

							<ButtonContainer>
								<Button
									onClick={handleOpenStripe}
									$variant="outline"
									size="l"
									sizeTablet="l"
									sizeMobile="m"
								>
									{t("stripeManagement:chargesEnabled.dashboard")}
									<ArrowRightUpIcon fill={theme.colors.defaultMain} />
								</Button>
								<Button
									onClick={openDisconnectConfirmModal}
									$variant="ghostDanger"
									size="l"
									sizeTablet="l"
									sizeMobile="m"
								>
									{t("stripeManagement:chargesEnabled.disconnect")}
								</Button>
							</ButtonContainer>
						</StripeInfoCard>

						{renderMetricsCard()}
					</StripeManagementContainer>
				);
			} else {
				// Versión simple sin métricas
				return (
					<StripeInfoCard $plain={$plain}>
						<Wrapper>
							<StripeHeader>
								<StripeIconContainer>
									<StripeLogo />
								</StripeIconContainer>
								<div>
									<Typography
										as="h3"
										$variant="body-1-semibold"
										$variantMobile="body-2-semibold"
									>
										{t("stripeManagement:chargesEnabled.title")}
									</Typography>
									<StatusContainer>
										<StatusIndicator $isActive={true} />
										<Typography
											as="span"
											$variant="body-2-medium"
											color={theme.colors.successMain}
										>
											{t("stripeManagement:status.active")}
										</Typography>
									</StatusContainer>
								</div>
							</StripeHeader>
							<StripeExplainer>
								<Typography
									as="p"
									$variant="body-1-semibold"
									color={theme.colors.defaultMain}
								>
									{t("stripeManagement:chargesEnabled.description")}
								</Typography>
								{stripeData.accountInfo && (
									<>
										<Typography
											as="p"
											$variant="body-1-medium"
											color={theme.colors.defaultWeak}
										>
											{t("stripeManagement:account.business")}:{" "}
											{stripeData.accountInfo.businessName}
										</Typography>
										<Typography
											as="p"
											$variant="body-1-medium"
											color={theme.colors.defaultWeak}
										>
											{t("stripeManagement:account.email")}:{" "}
											{stripeData.accountInfo.email}
										</Typography>
									</>
								)}
								<Typography
									as="p"
									$variant="body-3-medium"
									color={theme.colors.defaultWeak}
								>
									{t("stripeManagement:account.stripeId")}:{" "}
									{user?.stripeConnectedAccountId}
								</Typography>
							</StripeExplainer>
						</Wrapper>

						<ButtonContainer>
							<Button
								onClick={handleOpenStripe}
								$variant="outline"
								size="l"
								sizeTablet="l"
								sizeMobile="m"
							>
								{t("stripeManagement:chargesEnabled.dashboard")}
								<ArrowRightUpIcon fill={theme.colors.defaultMain} />
							</Button>
							<Button
								onClick={openDisconnectConfirmModal}
								$variant="ghostDanger"
								size="l"
								sizeTablet="l"
								sizeMobile="m"
							>
								{t("stripeManagement:chargesEnabled.disconnect")}
							</Button>
						</ButtonContainer>
					</StripeInfoCard>
				);
			}
		}

		return null;
	};

	// Renderizar componente principal con modales

	return (
		<>
			{renderMainContent()}

			{/* Modal de opciones de conexión */}
			{showConnectionOptionsModal && (
				<StripeConnectionOptionsModal
					isOpen={showConnectionOptionsModal}
					onClose={() => setShowConnectionOptionsModal(false)}
					onCreateNew={handleCreateNewAccount}
					onConnectExisting={handleConnectExistingAccount}
					onShowReactivation={handleShowReactivationModal}
					showReactivationOption={showReactivation && hasAvailableAccounts}
					isCreatingNew={isCreatingNew}
					isConnectingExisting={isConnectingExisting}
				/>
			)}

			{/* Modal de reactivación (cuentas previas) */}
			{showReactivationModal && (
				<ReactivationModal>
					<ReactivationContent>
						<StripeAccountReactivation
							onAccountReactivated={handleAccountReactivated}
							onCancel={() => setShowReactivationModal(false)}
						/>
					</ReactivationContent>
				</ReactivationModal>
			)}

			<Modal
				isOpen={showDisconnectConfirmModal}
				onClose={closeDisconnectConfirmModal}
				title={t("stripeManagement:disconnectConfirm.title")}
				maxWidth="480px"
			>
				<DisconnectModalBody>
					<Typography
						as="p"
						$variant="body-2-medium"
						color={theme.colors.defaultWeak}
					>
						{t("stripeManagement:disconnectConfirm.description")}
					</Typography>
					<DisconnectModalActions>
						<Button
							type="button"
							$variant="outline"
							size="l"
							onClick={closeDisconnectConfirmModal}
							disabled={isDisconnecting}
						>
							{t("stripeManagement:disconnectConfirm.cancel")}
						</Button>
						<Button
							type="button"
							$variant="ghostDanger"
							size="l"
							onClick={handleDisconnectStripe}
							disabled={isDisconnecting}
						>
							{isDisconnecting
								? t("stripeManagement:disconnectConfirm.loading")
								: t("stripeManagement:disconnectConfirm.confirm")}
						</Button>
					</DisconnectModalActions>
				</DisconnectModalBody>
			</Modal>
		</>
	);
};

// Styled Components
const DebugInfo = styled.div`
  background-color: ${({ theme }) => theme.fill.defaultSubtle};
  border-radius: ${({ theme }) => theme.radius.md};
  padding: ${({ theme }) => theme.sizing.sm};
`;

const StripeManagementContainer = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: ${({ theme }) => theme.sizing.sm};

  @media (max-width: ${({ theme }) => theme.breakpoints.md}) {
    display: flex;
    flex-direction: column;
  }
`;

const BaseCard = styled.div`
  // background: ${({ theme }) => theme.fill.defaultMain};
  ${({ $plain, theme }) =>
		!$plain &&
		`
    border: 1px solid ${theme.border.defaultWeak};
    border-radius: ${theme.radius.md};
  `}
  padding: ${({ theme }) => theme.sizing.sm};
  display: flex;
  flex-direction: column;
  justify-content: space-between;
  gap: ${({ theme }) => theme.sizing.sm};

  @media (max-width: ${({ theme }) => theme.breakpoints.md}) {
    padding: 12px;
  }
`;

const NoAccountCard = styled(BaseCard)``;
const PendingVerificationCard = styled(BaseCard)``;
const StripeInfoCard = styled(BaseCard)``;

const SimpleStripeSurface = styled.div`
  display: flex;
  flex-direction: column;
  width: 100%;
  gap: ${({ theme }) => theme.sizing.sm};
  padding: 0 ${({ theme }) => theme.sizing.sm} ${({ theme }) => theme.sizing.sm};
`;

const SimpleStripeCard = styled.div`
  display: flex;
  flex-direction: column;
  width: 100%;
  gap: ${({ theme }) => theme.sizing.sm};
  padding: ${({ theme }) => theme.sizing.sm};
  border: 1px solid ${({ theme }) => theme.border.defaultWeak};
  border-radius: ${({ theme }) => theme.radius.sm};
  background: ${({ theme }) => theme.fill.defaultMain};

  @media (max-width: ${({ theme }) => theme.breakpoints.md}) {
    padding: ${({ theme }) => theme.sizing.sm};
  }
`;

const SimpleStripeHeader = styled.div`
  display: flex;
  flex-direction: ${({ $stacked }) => ($stacked ? "column" : "row")};
  align-items: ${({ $stacked }) => ($stacked ? "flex-start" : "flex-start")};
  gap: ${({ theme }) => theme.sizing.sm};
`;

const SimpleStripeIconSlot = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
	border-radius: ${({ theme }) => theme.radius.xxs};
	overflow: hidden;	
`;

const SimpleStripeHeaderCopy = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${({ theme }) => theme.sizing.xxxs};
  min-width: 0;
`;

const SimpleStripeBody = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${({ theme }) => theme.sizing.xxs};
`;

const SimpleStripeActions = styled.div`
  display: flex;
  align-items: center;
  justify-content: ${({ $hasSecondary }) =>
		$hasSecondary ? "space-between" : "flex-start"};
  gap: ${({ theme }) => theme.sizing.sm};
  flex-wrap: wrap;

  @media (max-width: ${({ theme }) => theme.breakpoints.sm}) {
    flex-direction: column;
    align-items: stretch;
  }
`;

const ActionButton = styled(Button)`
  width: auto;

  @media (max-width: ${({ theme }) => theme.breakpoints.sm}) {
    width: 100%;
  }
`;

const DisconnectButton = styled(Button)`
  // padding: 0;

  @media (max-width: ${({ theme }) => theme.breakpoints.sm}) {
    align-self: center;
  }
`;

const SimpleStripeNote = styled.div`
  display: flex;
  align-items: flex-start;
  gap: ${({ theme }) => theme.sizing.xs};
`;

const MetricsCard = styled(BaseCard)`
  position: relative;
`;

const Wrapper = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${({ theme }) => theme.sizing.xs};
`;

const StripeHeader = styled.div`
  display: flex;
  align-items: center;
  gap: ${({ theme }) => theme.sizing.sm};
`;

const StripeIconContainer = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 48px;
  height: 48px;
  background: ${({ theme }) => theme.fill.defaultWeak};
  border-radius: ${({ theme }) => theme.radius.xs};
  overflow: hidden;
`;

const StatusContainer = styled.div`
  display: flex;
  align-items: center;
  gap: ${({ theme }) => theme.sizing.xxs};
`;

const StatusIndicator = styled.div`
  width: 8px;
  height: 8px;
  border-radius: 50%;
  background-color: ${({ $isActive, theme }) =>
		$isActive ? theme.colors.successMain : theme.colors.warningMain};
`;

const StripeExplainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${({ theme }) => theme.sizing.xxs};
  padding-top: ${({ theme }) => theme.sizing.xs};
  padding-bottom: ${({ theme }) => theme.sizing.xs};
`;

const ButtonContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${({ theme }) => theme.sizing.sm};
  flex-wrap: wrap;

  @media (min-width: ${({ theme }) => theme.breakpoints.sm}) {
    flex-direction: row;
  }
`;

const RefreshButton = styled(Button)`
  position: absolute;
  top: ${({ theme }) => theme.sizing.xs};
  right: ${({ theme }) => theme.sizing.xs};
`;

const MetricsGrid = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: ${({ theme }) => theme.sizing.sm};

  @media (max-width: ${({ theme }) => theme.breakpoints.sm}) {
    grid-template-columns: 1fr;
  }
`;

const MetricItem = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${({ theme }) => theme.sizing.xxs};
`;

const DisconnectModalBody = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${({ theme }) => theme.sizing.md};
  padding: ${({ theme }) => theme.sizing.md};
`;

const DisconnectModalActions = styled.div`
  display: flex;
  justify-content: flex-end;
  gap: ${({ theme }) => theme.sizing.sm};
  flex-wrap: wrap;
`;

const ReactivationModal = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.5);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1000;
`;

const ReactivationContent = styled.div`
  background: white;
  border-radius: ${({ theme }) => theme.radius.md};
  max-width: 90vw;
  max-height: 90vh;
  overflow: auto;
`;

export default StripeManagement;
